import { Controller } from '@hotwired/stimulus'

const MAX_IMAGES = 3

export default class extends Controller {
  static values = { i18n: Object }

  static targets = [
    'title', 'category', 'brand', 'model', 'description', 'condition',
    'budgetMode', 'budgetInput', 'budgetCents', 'budgetPanel', 'openBudgetPanel',
    'municipality', 'neighborhood', 'imageInput', 'photoSlot',
    'previewTitle', 'previewCategory', 'previewDescription', 'previewBudget',
    'previewCondition', 'previewPlace'
  ]

  connect() {
    this.selectedFiles = []
    this.objectUrls = []
    this.budgetModeChanged()
    this.updatePreview()
  }

  disconnect() {
    this.revokeObjectUrls()
  }

  updatePreview() {
    const title = this.titleTarget.value.trim()
    const brandModel = [this.brandTarget.value.trim(), this.modelTarget.value.trim()].filter(Boolean).join(' ')
    const description = this.descriptionTarget.value.trim()

    this.previewTitleTarget.textContent = title || brandModel || this.i18nValue.preview_title
    this.previewTitleTarget.classList.toggle('is-placeholder', !title && !brandModel)
    this.previewDescriptionTarget.textContent = description || (title && brandModel ? brandModel : this.i18nValue.preview_body)
    this.previewCategoryTarget.textContent = this.selectedOptionText(this.categoryTarget) || this.i18nValue.preview_category
    this.previewConditionTarget.textContent = this.selectedConditionText() || this.i18nValue.preview_condition

    const city = this.selectedOptionText(this.municipalityTarget).split(',')[0]
    this.previewPlaceTarget.textContent = [this.neighborhoodTarget.value.trim(), city].filter(Boolean).join(', ') || this.i18nValue.default_municipality
    this.updatePreviewBudget()
  }

  budgetModeChanged() {
    const open = this.budgetModeTargets.find((radio) => radio.checked)?.value === 'open'
    this.budgetPanelTarget.hidden = open
    this.openBudgetPanelTarget.hidden = !open
    this.budgetInputTarget.required = !open

    if (open) {
      this.budgetCentsTarget.value = ''
    } else {
      this.syncBudget()
    }

    this.updatePreviewBudget()
  }

  budgetChanged() {
    const digits = this.budgetInputTarget.value.replace(/\D/g, '')
    this.budgetInputTarget.value = digits ? Number(digits).toLocaleString('pt-BR') : ''
    this.syncBudget()
    this.updatePreviewBudget()
  }

  submit() {
    if (this.currentBudgetMode() === 'ceiling') this.syncBudget()
  }

  chooseImages(event) {
    const index = Number(event.currentTarget.dataset.index)
    if (this.selectedFiles[index]) {
      this.selectedFiles.splice(index, 1)
      this.syncImageInput()
      this.renderPhotoSlots()
    } else {
      this.imageInputTarget.click()
    }
  }

  imagesChanged() {
    const additions = Array.from(this.imageInputTarget.files)
    const knownFiles = new Set(this.selectedFiles.map((file) => this.fileKey(file)))

    const uniqueAdditions = additions.filter((file) => !knownFiles.has(this.fileKey(file)))
    const availableSlots = MAX_IMAGES - this.selectedFiles.length
    uniqueAdditions.slice(0, availableSlots).forEach((file) => this.selectedFiles.push(file))

    if (uniqueAdditions.length > availableSlots) {
      window.alert(this.i18nValue.maximum_images)
    }

    this.syncImageInput()
    this.renderPhotoSlots()
  }

  syncBudget() {
    const digits = this.budgetInputTarget.value.replace(/\D/g, '')
    if (!digits) {
      this.budgetCentsTarget.value = ''
      return
    }

    const cents = Number(digits) * 100
    this.budgetCentsTarget.value = Number.isSafeInteger(cents) ? cents.toString() : ''
  }

  updatePreviewBudget() {
    if (this.currentBudgetMode() === 'open') {
      this.previewBudgetTarget.textContent = this.i18nValue.open_budget
      return
    }

    const amount = this.budgetInputTarget.value.trim()
    this.previewBudgetTarget.textContent = amount
      ? this.interpolate(this.i18nValue.budget, { AMOUNT: amount })
      : this.i18nValue.empty_budget
  }

  currentBudgetMode() {
    return this.budgetModeTargets.find((radio) => radio.checked)?.value || 'ceiling'
  }

  selectedOptionText(select) {
    const option = select.selectedOptions[0]
    return option?.value ? option.textContent.trim() : ''
  }

  selectedConditionText() {
    return this.conditionTargets.find((input) => input.checked)?.dataset.label || ''
  }

  syncImageInput() {
    if (typeof DataTransfer === 'undefined') return

    const transfer = new DataTransfer()
    this.selectedFiles.forEach((file) => transfer.items.add(file))
    this.imageInputTarget.files = transfer.files
  }

  renderPhotoSlots() {
    this.revokeObjectUrls()

    this.photoSlotTargets.forEach((slot, index) => {
      const file = this.selectedFiles[index]
      slot.replaceChildren()

      if (file) {
        const objectUrl = URL.createObjectURL(file)
        this.objectUrls.push(objectUrl)
        const image = document.createElement('img')
        image.src = objectUrl
        image.alt = ''

        const remove = document.createElement('span')
        remove.className = 'request-photo-slot__remove'
        remove.textContent = this.i18nValue.remove

        slot.append(image, remove)
        slot.classList.add('has-image')
        slot.setAttribute('aria-label', this.interpolate(this.i18nValue.remove_image, {
          NUMBER: index + 1,
          FILENAME: file.name
        }))
      } else {
        slot.append(this.imageIcon(), this.addLabel())
        slot.classList.remove('has-image')
        slot.setAttribute('aria-label', this.interpolate(this.i18nValue.add_image, { NUMBER: index + 1 }))
      }
    })
  }

  imageIcon() {
    const namespace = 'http://www.w3.org/2000/svg'
    const svg = document.createElementNS(namespace, 'svg')
    svg.setAttribute('width', '22')
    svg.setAttribute('height', '22')
    svg.setAttribute('viewBox', '0 0 24 24')
    svg.setAttribute('fill', 'none')
    svg.setAttribute('aria-hidden', 'true')
    svg.innerHTML = '<rect x="3" y="4" width="18" height="16" rx="2"/><circle cx="8.5" cy="9" r="1.6"/><path d="m21 16-4.5-4.5L7 21"/>'
    return svg
  }

  addLabel() {
    const label = document.createElement('span')
    label.textContent = this.i18nValue.add
    return label
  }

  interpolate(template, values) {
    return Object.entries(values).reduce(
      (text, [key, value]) => text.replaceAll(`__${key}__`, value),
      template
    )
  }

  fileKey(file) {
    return `${file.name}:${file.size}:${file.lastModified}`
  }

  revokeObjectUrls() {
    this.objectUrls.forEach((url) => URL.revokeObjectURL(url))
    this.objectUrls = []
  }
}
